import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { queryKeys } from "#/hooks/query-keys";
import { fetchPost, type Post } from "#/lib/api/posts";
import { SUPPORTED_LOCALES, type SupportedLocale } from "#/lib/locale";
import { localeAtom } from "#/stores/locale-store";

/** 훅과 라우트 로더(SSR ensureQueryData)가 공유하는 쿼리 정의 — 키/fetch 드리프트 방지. */
export function learnPostQueryOptions(slug: string, locale: SupportedLocale) {
	return queryOptions({
		queryKey: queryKeys.posts.detail(slug, locale),
		queryFn: () => fetchPost(slug, locale),
		staleTime: 60 * 60 * 1000,
		gcTime: 24 * 60 * 60 * 1000,
	});
}

/**
 * One learn post by slug in the active locale.
 * Data semantics: `undefined` = loading, `Post` = loaded, `null` = not found
 * (unknown slug or draft — an expected render state, not an error).
 *
 * Placeholder 체인 (로더 깜빡임 방지 — 항상 "같은 slug"만, 다른 글 잔상 금지):
 * ① 같은 slug의 이전 locale 데이터 (같은 인스턴스 안의 키 전환)
 * ② 현재 locale 목록 캐시 (허브 → 글 즉시 렌더)
 * ③ 다른 locale의 같은 slug 상세/목록 캐시 — en↔ko 전환은 라우트 트리를
 *    갈아타며 리마운트되어 ①이 끊기므로 캐시에서 직접 폴백
 */
export function useLearnPost(slug: string) {
	const locale = useAtomValue(localeAtom);
	const queryClient = useQueryClient();
	return useQuery({
		...learnPostQueryOptions(slug, locale),
		placeholderData: (previousData, previousQuery) => {
			// queryKey = ["posts", "detail", slug, locale]
			const previousSlug = previousQuery?.queryKey[2];
			if (previousSlug === slug && previousData) {
				return previousData;
			}
			const findInList = (targetLocale: string) =>
				queryClient
					.getQueryData<Post[]>(queryKeys.posts.list(targetLocale))
					?.find((post) => post.slug === slug);
			const fromCurrentList = findInList(locale);
			if (fromCurrentList) {
				return fromCurrentList;
			}
			for (const other of SUPPORTED_LOCALES) {
				if (other === locale) continue;
				const cached =
					queryClient.getQueryData<Post | null>(
						queryKeys.posts.detail(slug, other),
					) ?? findInList(other);
				if (cached) {
					return cached;
				}
			}
			return undefined;
		},
	});
}
